import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtDate, fmtDateTime } from "../sectionUtils";
import s from "./SectionFacilitiesTab.module.css";

const MAINT_STATUS = {
  OPEN:        { bg: "#fde8e4", color: "#c0392b", label: "Open" },
  IN_PROGRESS: { bg: "#fdf3d4", color: "#8a6d28", label: "In Progress" },
  RESOLVED:    { bg: "#e8f6ed", color: "#1e6f3e", label: "Resolved" },
};
const DAY_ORDER = ["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY","SUNDAY","DAILY"];

export default function SectionFacilitiesTab({ section }) {
  const [room,          setRoom]          = useState(null);
  const [cleaning,      setCleaning]      = useState([]);
  const [maintenance,   setMaintenance]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");

  useEffect(() => {
    if (!section?.id) return;
    let cancelled = false;
    setLoading(true); setError("");

    const homeroomId = section.homeroomId;

    Promise.all([
      homeroomId
        ? fetch(`${API_BASE}/api/rooms/${homeroomId}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null),
      homeroomId
        ? fetch(`${API_BASE}/api/cleaning-schedules?areaId=${homeroomId}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : []).catch(() => [])
        : Promise.resolve([]),
      homeroomId
        ? fetch(`${API_BASE}/api/maintenance-requests?areaId=${homeroomId}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : []).catch(() => [])
        : Promise.resolve([]),
    ]).then(([roomData, cleanData, maintData]) => {
      if (cancelled) return;
      setRoom(roomData);
      setCleaning(Array.isArray(cleanData) ? cleanData : []);
      setMaintenance(Array.isArray(maintData) ? maintData : []);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setError("Could not load facility data."); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [section?.id, section?.homeroomId]);

  if (loading) return <div className={s.loading}>Loading facility data…</div>;
  if (error)   return <div className={s.errorBanner}>{error}</div>;

  if (!section?.homeroomId) return (
    <div className={s.empty}>
      <div className={s.emptyIcon}>🏗️</div>
      <div className={s.emptyText}>No homeroom assigned to this section.</div>
    </div>
  );

  const openRequests     = maintenance.filter(m => m.status !== "RESOLVED");
  const resolvedRequests = maintenance.filter(m => m.status === "RESOLVED");
  const sortedCleaning   = [...cleaning].sort((a, b) =>
    DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek)
  );

  return (
    <div className={s.root}>
      {/* Room details */}
      {room && (
        <div className={s.section}>
          <div className={s.sectionHead}>
            <span aria-hidden="true">📍</span> Room Details
          </div>
          <div className={s.roomCard}>
            <div className={s.roomName}>{room.name}</div>
            <div className={s.roomMeta}>
              {room.building && <span>🏢 {room.building}</span>}
              {room.floor != null && <span>Floor {room.floor}</span>}
              {room.roomType && <span>{room.roomType}</span>}
              {room.capacity && <span>👥 {room.capacity} seats</span>}
            </div>
          </div>
        </div>
      )}

      {/* Cleaning schedule */}
      <div className={s.section}>
        <div className={s.sectionHead}>
          <span aria-hidden="true">🧹</span> Cleaning Schedule
          {sortedCleaning.length > 0 && (
            <span className={s.countBadge}>{sortedCleaning.length}</span>
          )}
        </div>
        {sortedCleaning.length === 0 ? (
          <div className={s.emptyInline}>No cleaning schedule on record.</div>
        ) : (
          <div className={s.cleaningList}>
            {sortedCleaning.map(c => (
              <div key={c.id} className={s.cleaningCard}>
                <span className={s.cleaningDay}>{c.dayOfWeek}</span>
                {c.timeSlot && <span className={s.cleaningTime}>🕐 {c.timeSlot}</span>}
                {c.frequency && (
                  <span className={s.cleaningFreq}>{c.frequency.toLowerCase().replace("_", " ")}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Open maintenance requests */}
      <div className={s.section}>
        <div className={s.sectionHead}>
          <span aria-hidden="true">🔧</span> Maintenance Issues
          {openRequests.length > 0 && (
            <span className={`${s.countBadge} ${s.countDanger}`}>{openRequests.length} open</span>
          )}
        </div>
        {openRequests.length === 0 && resolvedRequests.length === 0 ? (
          <div className={s.emptyInline}>No maintenance requests on record.</div>
        ) : (
          <div className={s.maintList}>
            {[...openRequests, ...resolvedRequests].map(m => {
              const badge = MAINT_STATUS[m.status] || MAINT_STATUS.OPEN;
              return (
                <div key={m.id} className={s.maintCard}>
                  <div className={s.maintTop}>
                    <span className={s.maintDesc}>{m.description || "No description"}</span>
                    <span
                      className={s.maintBadge}
                      style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                  <div className={s.maintMeta}>
                    {m.requestedByUsername && (
                      <span>Reported by {m.requestedByUsername}</span>
                    )}
                    {m.createdAt && <span>{fmtDate(m.createdAt)}</span>}
                    {m.resolvedAt && <span>Resolved {fmtDate(m.resolvedAt)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
